<template>
  <div class="modal" :class="{ 'is-active': visible }">
    <div class="modal-background" @click="handleClose"></div>
    <div class="modal-card two-factor-setup-modal">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ $t('twoFactor.setupTitle') || 'Set Up Two-Factor Authentication' }}</p>
        <button class="delete" aria-label="close" @click="handleClose"></button>
      </header>

      <section class="modal-card-body">
        <!-- Step 1: Show QR Code -->
        <div v-if="step === 'qr'" class="setup-step">
          <div class="qr-section">
            <p class="setup-instruction">
              {{ $t('twoFactor.scanQrCode') || 'Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)' }}
            </p>
            
            <div v-if="loading" class="qr-loading">
              <span class="loader"></span>
            </div>
            <div v-else-if="qrCode" class="qr-code">
              <img :src="qrCode" alt="QR Code" />
            </div>
            <p v-else class="help is-danger">
              QR code is unavailable. Use secret key below for manual setup.
            </p>
            
            <div v-if="secret" class="secret-section">
              <p class="secret-label">{{ $t('twoFactor.manualEntry') || "Can't scan? Enter this code manually:" }}</p>
              <div class="secret-code">
                <code>{{ secret }}</code>
                <button class="copy-button" @click="copySecret" :title="$t('common.copy') || 'Copy'">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>
            </div>
            <p v-else-if="provisioningUri" class="help is-info">
              If your app supports OTP URI import, use the provisioning URI from API response.
            </p>
          </div>
          
          <div class="verify-section">
            <p class="verify-instruction">
              {{ $t('twoFactor.enterCodeToVerify') || 'Enter the 6-digit code from your authenticator app to complete setup:' }}
            </p>
            <div class="field">
              <div class="control">
                <input
                  ref="codeInput"
                  v-model="verificationCode"
                  class="input code-input"
                  :class="{ 'is-danger': error }"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="6"
                  :placeholder="$t('twoFactor.codePlaceholder') || '000000'"
                  :disabled="verifying"
                  @keyup.enter="handleVerify"
                />
              </div>
              <p v-if="error" class="help is-danger">{{ error }}</p>
            </div>
          </div>
        </div>

        <!-- Step 2: Show Recovery Codes -->
        <div v-else-if="step === 'recovery'" class="setup-step">
          <div class="recovery-section">
            <div class="success-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3 class="recovery-title">{{ $t('twoFactor.enabled') || '2FA Enabled Successfully!' }}</h3>
            <p class="recovery-warning">
              {{ $t('twoFactor.saveRecoveryCodes') || 'Save these recovery codes in a safe place. You can use them to access your account if you lose your authenticator device.' }}
            </p>
            
            <div class="recovery-codes">
              <code v-for="(code, index) in recoveryCodes" :key="index" class="recovery-code">
                {{ code }}
              </code>
            </div>
            
            <div class="recovery-actions">
              <button class="button is-small" @click="copyRecoveryCodes">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                {{ $t('common.copy') || 'Copy' }}
              </button>
              <button class="button is-small" @click="downloadRecoveryCodes">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {{ $t('common.download') || 'Download' }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer class="modal-card-foot">
        <button v-if="step === 'qr'" class="button is-primary" :class="{ 'is-loading': verifying }" :disabled="!verificationCode || verifying" @click="handleVerify">
          {{ $t('twoFactor.verify') || 'Verify & Enable' }}
        </button>
        <button v-if="step === 'recovery'" class="button is-primary" @click="handleDone">
          {{ $t('common.done') || 'Done' }}
        </button>
        <button v-if="step === 'qr'" class="button" @click="handleClose">
          {{ $t('common.cancel') || 'Cancel' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import twoFactorApi from '../../api/twoFactor'

export default {
  name: 'TwoFactorSetup',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      step: 'qr',
      loading: false,
      verifying: false,
      qrCode: '',
      secret: '',
      provisioningUri: '',
      verificationCode: '',
      recoveryCodes: [],
      error: ''
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initSetup()
      } else {
        this.reset()
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.initSetup()
    }
  },
  methods: {
    normalizeSetupPayload(response) {
      const payload = response?.data?.data || response?.data || {}
      return {
        qrCode: payload.qr_code || payload.qrCode || payload.QRCode || '',
        secret: payload.secret || payload.Secret || '',
        provisioningUri: payload.provisioning_uri || payload.provisioningUri || payload.ProvisioningURI || ''
      }
    },
    async initSetup() {
      this.loading = true
      this.error = ''
      
      try {
        const response = await twoFactorApi.setup(this.$http)
        const data = this.normalizeSetupPayload(response)
        this.qrCode = data.qrCode
        this.secret = data.secret
        this.provisioningUri = data.provisioningUri

        if (!this.qrCode && !this.secret) {
          this.error = 'Cannot load 2FA setup data. Please try again.'
          return
        }
        
        this.$nextTick(() => {
          if (this.$refs.codeInput) {
            this.$refs.codeInput.focus()
          }
        })
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Failed to initialize 2FA setup'
      } finally {
        this.loading = false
      }
    },
    async handleVerify() {
      if (!this.verificationCode || this.verificationCode.length < 6) {
        this.error = this.$t('twoFactor.codeRequired') || 'Please enter a 6-digit code'
        return
      }

      this.verifying = true
      this.error = ''

      try {
        const response = await twoFactorApi.verify(this.$http, this.verificationCode)
        const data = response?.data?.data || response?.data || {}
        
        if (data.success) {
          this.recoveryCodes = data.recovery_codes || []
          this.step = 'recovery'
        } else {
          this.error = data.message || 'Verification failed'
        }
      } catch (err) {
        this.error = err.response?.data?.message || err.message || 'Invalid verification code'
      } finally {
        this.verifying = false
      }
    },
    copySecret() {
      navigator.clipboard.writeText(this.secret)
      this.$store.dispatch('NOTIFY', {
        type: 'success',
        text: this.$t('common.copied') || 'Copied to clipboard',
        duration: 2000
      })
    },
    copyRecoveryCodes() {
      navigator.clipboard.writeText(this.recoveryCodes.join('\n'))
      this.$store.dispatch('NOTIFY', {
        type: 'success',
        text: this.$t('common.copied') || 'Copied to clipboard',
        duration: 2000
      })
    },
    downloadRecoveryCodes() {
      const content = `CySIEM Recovery Codes\n${'='.repeat(30)}\n\n${this.recoveryCodes.join('\n')}\n\nKeep these codes safe!\nEach code can only be used once.`
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'cysiem-recovery-codes.txt'
      a.click()
      URL.revokeObjectURL(url)
    },
    handleDone() {
      this.$emit('success')
      this.handleClose()
    },
    handleClose() {
      this.$emit('close')
    },
    reset() {
      this.step = 'qr'
      this.loading = false
      this.verifying = false
      this.qrCode = ''
      this.secret = ''
      this.provisioningUri = ''
      this.verificationCode = ''
      this.recoveryCodes = []
      this.error = ''
    }
  }
}
</script>

<style lang="scss" scoped>
.two-factor-setup-modal {
  max-width: 480px;
  
  .modal-card-body {
    padding: 1.5rem;
  }
}

.setup-step {
  text-align: center;
}

.setup-instruction {
  margin-bottom: 1rem;
  color: #666;
}

.qr-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3273dc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.qr-code {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  
  img {
    max-width: 200px;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 0.5rem;
    background: white;
  }
}

.secret-section {
  margin: 1rem 0;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 4px;
}

.secret-label {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.secret-code {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  code {
    font-size: 1rem;
    font-family: monospace;
    letter-spacing: 0.1em;
    background: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
}

.copy-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #666;
  
  &:hover {
    color: #3273dc;
  }
}

.verify-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.verify-instruction {
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.code-input {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-family: monospace;
  max-width: 200px;
  margin: 0 auto;
}

.success-icon {
  color: #48c774;
  margin-bottom: 1rem;
}

.recovery-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.recovery-warning {
  color: #666;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.recovery-codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin: 1rem 0;
}

.recovery-code {
  display: block;
  font-family: monospace;
  font-size: 0.875rem;
  padding: 0.5rem;
  background: #f5f5f5;
  border-radius: 4px;
}

.recovery-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  
  .button {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
}

.modal-card-foot {
  justify-content: flex-end;
  gap: 0.5rem;
}

.help.is-danger {
  color: #f14668;
  text-align: center;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
